import {
  Cartesian3,
  Cesium3DTileset,
  defined,
  Entity,
  Model,
} from "@cesium/engine";
import EditObject from "./wrapper/EditObject";
import PointEntity from "./PointEntity";
import LinestringEntity from "./LinestringEntity";
import PolygonEntity from "./PolygonEntity";

/**
 * 객체 이동을 위한 기능 클래스. <br>
 * A functionality class for object movement. <br>
 * ※ 주의사항(Note). </br>
 * 일반 Entity 객체에서는 기능이 제한됩니다. <br>
 * Functionality is limited on regular Entity objects..
 * @alias EditMove
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 1-1. 입력된 좌표 값으로 모델 이동(위경도 좌표)
 * EditMove.setPositionFromDegrees(model, 126.9782349646649, 37.56671536989183, 150);
 * // 1-2. 입력된 좌표 값으로 모델 이동(데카르트 좌표)
 * EditMove.setPosition(model, new Ditap.Cartesian3(-3044811.6264139246, 4043796.6433953536, 3867466.40813661));
 * // 1-3. 입력된 좌표 값 만큼 모델 이동(데카르트 좌표)
 * EditMove.addPosition(model, new Ditap.Cartesian3(30, 10, 10));
 *
 * // 3. 마우스로 객체 이동
 * const interactionMove = new Ditap.InteractionMove(viewer);
 * interactionMove.on();
 */
class EditMove {
  constructor() {
    //super();
  }

  /**
   * 경도, 위도, 높이 값을 통해 객체 이동.<br>
   * Move an object through longitude, latitude, and height values.
   * @param {Model | Cesium3DTileset | Entity} object 이동할 객체.<br> The object to be moved.
   * @param {number} longitude 경도.<br> Longitude.
   * @param {number} latitude 위도.<br> Latitude.
   * @param {number} [height=0] 높이.<br> Height.
   * @returns {Cartesian3} 적용된 좌표.<br> Applied coordinates.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * EditMove.setPositionFromDegrees(model, 126.9782349646649, 37.56671536989183, 150);
   */
  static setPositionFromDegrees(object, longitude, latitude, height = 0) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error(
        "EditMove.setPositionFromDegrees object parameter is invalid"
      );
    }

    try {
      // Degrees -> Cartesian3 변환
      const position = Cartesian3.fromDegrees(longitude, latitude, height);

      // 오브젝트 좌표 업데이트
      const result = EditObject.setPosition(object, position);

      return result;
    } catch (error) {
      throw Error(`EditMove.setPositionFromDegrees ${error}`);
    }
  }

  /**
   * 객체 위치 변경.<br>
   * Change the model's position using Cartesian values.
   * @param {Model | Cesium3DTileset | Entity} object 위치 변경할 객체.<br> The object to be repositioned.
   * @param {Cartesian3 | Property} position 좌표값.<br> Coordinate value.
   * @returns {Cartesian3 | Property} 적용된 좌표.<br> Applied coordinates.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * EditMove.setPosition(model, new Ditap.Cartesian3(-3044811.6264139246, 4043796.6433953536, 3867466.40813661));
   */
  static setPosition(object, position) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditMove.setPosition object parameter is invalid");
    }

    if (!(position instanceof Cartesian3 || defined(position.getValue))) {
      throw Error("EditMove.setPosition position parameter is invalid");
    }

    try {
      // set object position
      const result = EditObject.setPosition(object, position);

      return result;
    } catch (error) {
      throw Error(`EditMove.setPosition ${error}`);
    }
  }

  /**
   * 입력된 좌표 값만큼 객체 이동.<br>
   * Move the object as much as the input coordinate value.
   * @param {Model | Cesium3DTileset | Entity} object 이동할 객체.<br> The object to be moved.
   * @param {Cartesian3} position 이동량.<br> Amount of movement
   * @returns {Cartesian3} 적용된 좌표.<br> Applied coordinates.
   * @example
   * const viewer = new Ditap.DitapViewer("ditapContainer");
   *
   * const destination = Ditap.Cartesian3.fromDegrees(
   *   126.978275264,
   *   37.566642192,
   *   400
   * );
   *
   * // 카메라 이동
   * viewer.scene.camera.flyTo({
   *   destination: destination,
   * });
   *
   * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
   *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
   * );
   * const model = await Ditap.Model.fromGltfAsync{
   *  id: "model_01",
   *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
   *  modelMatrix: modelMatrix,
   *  scale: 3,
   * });
   * viewer.scene.primitives.add(model);
   *
   * EditMove.addPosition(model, new Ditap.Cartesian3(10, 20, 30));
   */
  static addPosition(object, position) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditMove.addPosition object parameter is invalid");
    }

    if (!(position instanceof Cartesian3)) {
      throw Error("EditMove.addPosition position parameter is invalid");
    }

    try {
      // 오브젝트 현재 좌표
      const objectPosition = EditObject.getPosition(object);

      // 오브젝트 현재 좌표 + 이동량
      const sum = Cartesian3.add(position, objectPosition, new Cartesian3());

      // 오브젝트 좌표 업데이트
      const result = EditObject.setPosition(object, sum);

      return result;
    } catch (error) {
      throw Error(`EditMove.addPosition ${error}`);
    }
  }

  /**
   * 객체의 중심 좌표값.<br>
   * Center coordinate of the object
   * @param {Model | Cesium3DTileset | Entity} object 좌표값을 구할 객체.<br> Object to obtain coordinates.
   * @returns {Cartesian3} 좌표값.<br> coordinates.
   */
  static getPosition(object) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditMove.getPosition object parameter is invalid");
    }

    try {
      const position = EditObject.getPosition(object);

      return position;
    } catch (error) {
      throw Error(`EditMove.getPosition ${error}`);
    }
  }

  /**
   * 객체의 트랜스폼 추출.<br>
   * Extracting object's transform
   * @param {Model | Cesium3DTileset | Entity} object 트랜스폼을 구할 객체.<br> Object to obtain transform.
   * @param {boolean} [enu=true] East-North-Up 기준 추출.<br> East-North-Up criteria extraction.
   * @returns {{
   *  position: Cartesian3,
   *  originalPosition: Cartesian3,
   *  translation: Cartesian3,
   *  quaternion: Quaternion,
   *  HeadingPitchRoll: HeadingPitchRoll,
   *  scale: Cartesian3
   * }} 트랜스폼 정보.<br> Transform info.
   */
  static decomposite(object, enu = true) {
    if (
      !(
        object instanceof Model ||
        object instanceof Cesium3DTileset ||
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      )
    ) {
      throw Error("EditMove.decomposite object parameter is invalid");
    }

    try {
      const decomposite = EditObject.decomposite(object, enu);

      return decomposite;
    } catch (error) {
      throw Error(`EditMove.decomposite ${error}`);
    }
  }
}

export default EditMove;
